import {
  type AssetFilterOptionsResponse,
  type GetAssetFilterOptionsAssetFilterOptionsGetQueryParams,
  useGetAssetFilterOptionsAssetFilterOptionsGet,
} from "kubb";
import type { Option } from "../components/Forms/SelectCombo/SelectCombo";

/**
 * Hook for fetching asset filter options with cascading filter support.
 *
 * Each facet returns all available values for that field, with counts
 * reflecting how many assets match with all OTHER filters applied.
 *
 * Gateway-only facets (configuration_state, connectivity) return null
 * when no gateways match the current filters.
 */
export function useAssetFilterOptions(
  params: GetAssetFilterOptionsAssetFilterOptionsGetQueryParams,
  options?: { enabled?: boolean },
) {
  const query = useGetAssetFilterOptionsAssetFilterOptionsGet(params, {
    query: {
      enabled: options?.enabled ?? true,
      staleTime: 30000, // Cache for 30 seconds
      placeholderData: (previousData) => previousData, // Show previous data while fetching
    },
  });

  return {
    ...query,
    data: query.data?.data,
    isError: query.isError,
    error: query.error,
  };
}

/**
 * Convert filter options to SelectCombo options format.
 */
export function filterOptionsToSelectOptions(
  options: AssetFilterOptionsResponse["manufacturer"] | null | undefined,
): Option[] {
  if (!options) return [];
  return options.map((opt) => ({
    label: `${opt.value} (${opt.count})`,
    value: opt.value,
  }));
}
